/**
 * Fare calculation — a single source of truth shared by the review, payment, and
 * confirmation steps so totals never drift between screens.
 *
 * Model: adults & children pay the full per-traveller fare; infants pay a reduced rate.
 * Taxes are a percentage of the base fare; a flat service fee is added per booking.
 */

export const TAX_RATE = 0.15; // 15% of base fare
export const SERVICE_FEE = 1500; // flat, per booking
export const INFANT_RATE = 0.1; // infants pay 10% of the fare

/**
 * @param {import('../mock/types').Flight} flight
 * @param {{adults?:number, children?:number, infants?:number}} criteria
 * @returns {import('../mock/types').FareBreakdown & {
 *   perFare:number, adults:number, children:number, infants:number,
 *   adultTotal:number, childTotal:number, infantTotal:number, infantRate:number
 * }}
 */
export function computeFare(flight, criteria = {}) {
  const perFare = flight ? flight.finalPrice : 0;
  const adults = Math.max(1, criteria.adults || 1);
  const children = criteria.children || 0;
  const infants = criteria.infants || 0;

  const adultTotal = perFare * adults;
  const childTotal = perFare * children;
  const infantTotal = Math.round(perFare * INFANT_RATE) * infants;

  const baseFare = adultTotal + childTotal + infantTotal;
  const taxes = Math.round(baseFare * TAX_RATE);
  const serviceFee = SERVICE_FEE;
  const total = baseFare + taxes + serviceFee;

  return {
    baseFare,
    taxes,
    serviceFee,
    total,
    currency: flight ? flight.currency : "PKR",
    perFare,
    adults,
    children,
    infants,
    adultTotal,
    childTotal,
    infantTotal,
    infantRate: INFANT_RATE,
  };
}

/** Format an amount like "PKR 52,206". */
export const formatMoney = (amount, currency = "PKR") =>
  `${currency} ${Math.round(amount).toLocaleString("en-PK")}`;
